"use client";

import { useEffect, useRef, useState } from "react";
import { onIdentitySessionExpired } from "@cede/utils";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useSessionExpiredNoticeStore } from "@/store/sessionExpiredNoticeStore";
import * as Sentry from "@sentry/nextjs";
import { LogIn } from "lucide-react";

/** Shown when the API client could not refresh the identity token, so a dead session is never a broken page. */
export function SessionExpiredBanner() {
  const isVisible = useSessionExpiredNoticeStore((state) => state.isVisible);
  const hide = useSessionExpiredNoticeStore((state) => state.hide);

  useEffect(
    () =>
      onIdentitySessionExpired(() => {
        const notice = useSessionExpiredNoticeStore.getState();
        if (notice.isVisible) return;
        notice.show();
        Sentry.captureMessage("Auth session expired", { level: "warning", tags: { app: "hub" } });
      }),
    [],
  );

  return isVisible ? <SessionExpiredCard onSignInOpened={hide} /> : null;
}

function SessionExpiredCard({ onSignInOpened }: { onSignInOpened: () => void }) {
  const { authenticated, login, logout } = useHubUser();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const isSignInPendingRef = useRef(false);
  const signInButtonRef = useRef<HTMLButtonElement>(null);

  // The alert is announced but not focused; this puts its only action in keyboard reach.
  useEffect(() => {
    signInButtonRef.current?.focus();
  }, []);

  // Privy ignores login while a render still holds the old user, so sign-in opens on the signed-out render.
  useEffect(() => {
    if (authenticated || !isSignInPendingRef.current) return;
    isSignInPendingRef.current = false;
    onSignInOpened();
    login();
  }, [authenticated, login, onSignInOpened]);

  const handleSignInAgain = async () => {
    if (!authenticated) {
      onSignInOpened();
      login();
      return;
    }
    setIsSigningOut(true);
    isSignInPendingRef.current = true;
    try {
      await logout();
    } catch (error) {
      isSignInPendingRef.current = false;
      Sentry.captureException(error, { tags: { app: "hub" } });
    }
    // Signed out, the card closes on the next render; still signed in, the button comes back for a retry.
    setIsSigningOut(false);
  };

  return (
    <div
      role="alert"
      className="fixed z-snackbar left-1/2 top-[100px] -translate-x-1/2 p-3 w-[calc(100vw-2rem)] max-w-sm rounded-xl bg-cede-warning/10 backdrop-blur-md shadow-lg border border-cede-warning/30"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">
          <LogIn className="h-5 w-5 text-cede-warning" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-cede-text-primary">
            Your session expired. Sign in again to keep trading.
          </p>

          <div className="mt-3 flex items-center gap-2">
            <button
              ref={signInButtonRef}
              type="button"
              onClick={() => void handleSignInAgain()}
              disabled={isSigningOut}
              className="rounded-lg bg-cede-warning px-3 py-1.5 text-xs font-medium text-cede-warning-foreground hover:bg-cede-warning/90 transition-colors disabled:cursor-wait disabled:opacity-60"
            >
              Sign in again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
